import { useCreateOrUpdateMultipleSampleInAuction } from "@/api/related-fields";
import { FormLayoutElement } from "@/config/forms/general";
import { MultipleSampleInAuction } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const MultiplesampleInAuctionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: MultipleSampleInAuction;
  queryId: string;
  type: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z.object({
    multiplesampleDansVentesDescriptionNumeroLot: z.string().optional(),
    multiplesampleDansVentesDescriptionReproduitPage: z.string().optional(),
    multiplesampleDansVentesDescriptionEstimationHaute: z.string().optional(),
    multiplesampleDansVentesDescriptionEstimationBasse: z.string().optional(),
    multiplesampleDansVentesDescriptionVendu: z.boolean().optional(),
    multiplesampleDansVentesDescriptionResultatNonListe: z.boolean().optional(),
    multiplesampleDansVentesDescriptionNonVenu: z.boolean().optional(),
    multiplesampleDansVentesDescriptionNonVendu: z.boolean().optional(),
    multiplesampleDansVentesDescriptionNonReproduit: z.boolean().optional(),
    multiplesampleDansVentesDescriptionRetire: z.boolean().optional(),
    multiplesampleDansVentesDescriptionMiseAPrix: z.string().optional(),
    multiplesampleDansVentesDescriptionPrixMarteau: z.string().optional(),
    multiplesampleDansVentesDescriptionPrixAvecFrais: z.string().optional(),
    multiplesampleDansVentesDescriptionNotes: z.string().optional(),
    auction: z
      .object({
        ventesDescriptionNumeroVente: z.string().optional(),
        ventesDescriptionResume: z.string().optional(),
      })
      .optional(),
  });

  const { mutate: createOrUpdateMultipleSampleInAuction } =
    useCreateOrUpdateMultipleSampleInAuction(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      auction: data.auction,
      multiplesampleDansVentesDescriptionNumeroLot:
        data.multiplesampleDansVentesDescriptionNumeroLot,
      multiplesampleDansVentesDescriptionReproduitPage:
        data.multiplesampleDansVentesDescriptionReproduitPage,
      multiplesampleDansVentesDescriptionEstimationHaute:
        data.multiplesampleDansVentesDescriptionEstimationHaute,
      multiplesampleDansVentesDescriptionEstimationBasse:
        data.multiplesampleDansVentesDescriptionEstimationBasse,
      multiplesampleDansVentesDescriptionVendu:
        data.multiplesampleDansVentesDescriptionVendu,
      multiplesampleDansVentesDescriptionResultatNonListe:
        data.multiplesampleDansVentesDescriptionResultatNonListe,
      multiplesampleDansVentesDescriptionNonVenu:
        data.multiplesampleDansVentesDescriptionNonVenu,
      multiplesampleDansVentesDescriptionNonVendu:
        data.multiplesampleDansVentesDescriptionNonVendu,
      multiplesampleDansVentesDescriptionNonReproduit:
        data.multiplesampleDansVentesDescriptionNonReproduit,
      multiplesampleDansVentesDescriptionRetire:
        data.multiplesampleDansVentesDescriptionRetire,
      multiplesampleDansVentesDescriptionMiseAPrix:
        data.multiplesampleDansVentesDescriptionMiseAPrix,
      multiplesampleDansVentesDescriptionPrixMarteau:
        data.multiplesampleDansVentesDescriptionPrixMarteau,
      multiplesampleDansVentesDescriptionPrixAvecFrais:
        data.multiplesampleDansVentesDescriptionPrixAvecFrais,
      multiplesampleDansVentesDescriptionNotes:
        data.multiplesampleDansVentesDescriptionNotes,
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    const emptyStr = (v: string | undefined) => v ?? "";
    const emptyBool = (v: boolean | undefined) => v ?? false;
    createOrUpdateMultipleSampleInAuction({
      id: data.id,
      multiplesampleId: data.multiplesample.id,
      auctionId: data.auction.id,
      multiplesampleDansVentesDescriptionNumeroLot: emptyStr(
        formData.multiplesampleDansVentesDescriptionNumeroLot,
      ),
      multiplesampleDansVentesDescriptionReproduitPage: emptyStr(
        formData.multiplesampleDansVentesDescriptionReproduitPage,
      ),
      multiplesampleDansVentesDescriptionEstimationHaute: emptyStr(
        formData.multiplesampleDansVentesDescriptionEstimationHaute,
      ),
      multiplesampleDansVentesDescriptionEstimationBasse: emptyStr(
        formData.multiplesampleDansVentesDescriptionEstimationBasse,
      ),
      multiplesampleDansVentesDescriptionVendu: emptyBool(
        formData.multiplesampleDansVentesDescriptionVendu,
      ),
      multiplesampleDansVentesDescriptionResultatNonListe: emptyBool(
        formData.multiplesampleDansVentesDescriptionResultatNonListe,
      ),
      multiplesampleDansVentesDescriptionNonVenu: emptyBool(
        formData.multiplesampleDansVentesDescriptionNonVenu,
      ),
      multiplesampleDansVentesDescriptionNonVendu: emptyBool(
        formData.multiplesampleDansVentesDescriptionNonVendu,
      ),
      multiplesampleDansVentesDescriptionNonReproduit: emptyBool(
        formData.multiplesampleDansVentesDescriptionNonReproduit,
      ),
      multiplesampleDansVentesDescriptionRetire: emptyBool(
        formData.multiplesampleDansVentesDescriptionRetire,
      ),
      multiplesampleDansVentesDescriptionMiseAPrix: emptyStr(
        formData.multiplesampleDansVentesDescriptionMiseAPrix,
      ),
      multiplesampleDansVentesDescriptionPrixMarteau: emptyStr(
        formData.multiplesampleDansVentesDescriptionPrixMarteau,
      ),
      multiplesampleDansVentesDescriptionPrixAvecFrais: emptyStr(
        formData.multiplesampleDansVentesDescriptionPrixAvecFrais,
      ),
      multiplesampleDansVentesDescriptionNotes: emptyStr(
        formData.multiplesampleDansVentesDescriptionNotes,
      ),
    });
  };

  return (
    <LinksDrawerLayout
      form={form}
      fields={fields ?? []}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={{
        attachments: data.multiplesample.attachments ?? [],
        type: "multiplesample",
        alt:
          data.multiplesample
            .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
      }}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default MultiplesampleInAuctionDrawer;
